import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldMessageComponent } from '../field-message/field-message.component';

/**
 * Label/value option accepted by the shared radio group component.
 */
export interface RadioOption {
  /** Submitted form value. */
  value: string;
  /** Human-readable option label. */
  label: string;
}

/**
 * Radio group form control that integrates with Angular forms.
 */
@Component({
  selector: 'app-field-radio-group',
  imports: [FieldMessageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldRadioGroupComponent),
      multi: true,
    },
  ],
  templateUrl: './field-radio-group.component.html',
  styleUrl: './field-radio-group.component.css',
})
export class FieldRadioGroupComponent implements ControlValueAccessor {
  /** Incrementing suffix used to create unique native radio group names. */
  private static _uid = 0;

  /** Group label. A trailing `*` marks the field as required. */
  readonly label        = input('');
  /** Radio option labels and submitted values. */
  readonly options      = input<Array<string | RadioOption>>([]);
  /** Visual layout for the radio options. */
  readonly layout       = input<'vertical' | 'horizontal'>('vertical');
  /** Whether the field is currently invalid. */
  readonly invalid      = input(false);
  /** Validation error text. */
  readonly errorMessage = input('');

  /** Current selected option. */
  readonly selected   = signal('');
  /** Disabled state received from Angular forms. */
  readonly isDisabled = signal(false);

  /** Auto-generated name ensures radio inputs in one group stay grouped. */
  readonly groupName = `fr-group-${FieldRadioGroupComponent._uid++}`;

  /** DOM id prefix derived from the generated group name. */
  readonly groupId    = computed(() => `fr-${this.groupName}`);
  /** Label text with any required marker removed. */
  readonly labelText  = computed(() => this.label().replace(/\s*\*\s*$/, '').trim());
  /** Whether the label includes a required marker. */
  readonly isRequired = computed(() => this.label().includes('*'));

  optionValue(opt: string | RadioOption): string {
    return typeof opt === 'string' ? opt : opt.value;
  }

  optionLabel(opt: string | RadioOption): string {
    return typeof opt === 'string' ? opt : opt.label;
  }

  /** Registered Angular forms change callback. */
  private onChangeFn:  (v: string) => void = () => {};
  /** Registered Angular forms touched callback. */
  private onTouchedFn: () => void          = () => {};

  /** @inheritdoc */
  writeValue(v: string): void              { this.selected.set(v ?? ''); }
  /** @inheritdoc */
  registerOnChange(fn: (v: string) => void): void { this.onChangeFn = fn; }
  /** @inheritdoc */
  registerOnTouched(fn: () => void): void         { this.onTouchedFn = fn; }
  /** @inheritdoc */
  setDisabledState(d: boolean): void              { this.isDisabled.set(d); }

  /**
   * Selects an option and forwards the value to Angular forms.
   *
   * @param value Option value selected by the user.
   */
  select(value: string): void {
    if (this.isDisabled()) return;
    this.selected.set(value);
    this.onChangeFn(value);
    this.onTouchedFn();
  }
}
